import { z } from 'zod';
export const PasswordSchema = z
    .string()
    .min(8, '비밀번호는 8자 이상이어야 합니다.')
    .refine((v) => /[A-Za-z]/.test(v) && /\d/.test(v), '영문과 숫자를 모두 포함해야 합니다.');
export const LoginRequestSchema = z.object({
    /** 로그인 식별자 — 이메일 또는 사번/SSO 아이디. DB users.email (citext) 컬럼에 저장. */
    email: z.string().min(1).max(254),
    password: z.string().min(1),
});
export const UserRoleSchema = z.enum(['admin', 'user']);
/** 세션 발급 시점의 인증 경로. local = 이메일/비밀번호, 그 외는 외부 provider. */
export const AuthProviderSchema = z.enum([
    'local',
    'google',
    'microsoft',
    'oidc',
    'saml',
    'ldap',
    'isign',
]);
export const PublicUserSchema = z.object({
    id: z.string().uuid(),
    /** 이메일 또는 사번/SSO 아이디. */
    email: z.string(),
    displayName: z.string(),
    role: UserRoleSchema,
    disabledAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    /** 사용자별 LLM API 릴레이 키 발급 권한. UI 메뉴 게이트에 사용. */
    apiRelayEnabled: z.boolean(),
    /** 시스템 전역 LLM API 릴레이 활성화 여부. 두 플래그 모두 true 여야 메뉴 노출. */
    llmApiRelayEnabledGlobal: z.boolean(),
    /** 이번 세션의 로그인 방식. 'local' 일 때만 비밀번호 변경 메뉴 노출. */
    authProvider: AuthProviderSchema,
});
export const ChangePasswordRequestSchema = z.object({
    current: z.string().min(1),
    next: PasswordSchema,
});
// ── 인증 설정 (admin) ────────────────────────────────────────────────────────
export const ExternalAuthProviderSchema = z.enum([
    'google',
    'microsoft',
    'oidc',
    'saml',
    'ldap',
    'isign',
]);
export const AuthConfigResponseSchema = z.object({
    activeExternalProvider: ExternalAuthProviderSchema.nullable(),
    localAdminIpAllowlist: z.array(z.string()),
    adminPagesIpRestricted: z.boolean(),
    /**
     * 외부 SSO provider 활성 시 `/login` 진입과 동시에 SSO start 로 자동 redirect.
     * `/login/admin` break-glass 는 영향 없음.
     */
    ssoAutoRedirect: z.boolean(),
    sessionEpoch: z.number().int().nonnegative(),
    /** 현재 요청자의 IP — UI 가 자기 차단 여부를 보여주기 위해. */
    currentIp: z.string(),
    /** Microsoft Entra 등록 여부. tenantId/clientId 는 마스킹 없이 노출, secret 은 hasSecret 만. */
    microsoft: z
        .object({
        tenantId: z.string(),
        clientId: z.string(),
        hasSecret: z.boolean(),
    })
        .nullable(),
    /** LDAP 등록 여부. bind 비밀번호는 hasBindCredential 만. */
    ldap: z
        .object({
        url: z.string(),
        baseDN: z.string(),
        bindDN: z.string(),
        userSearchFilter: z.string(),
        emailAttribute: z.string(),
        displayNameAttribute: z.string(),
        startTLS: z.boolean(),
        hasBindCredential: z.boolean(),
        /** 'search' (기본) — service account bind → 사용자 검색 → user-bind. 'direct' — 사용자 ID 로 바로 bind, search 없음. */
        bindMode: z.enum(['search', 'direct']),
        /** direct 모드 전용. `{username}` placeholder 가 폼 입력값으로 치환. 예: `corp\\{username}`, `{username}@corp.local`. */
        directBindFormat: z.string(),
    })
        .nullable(),
    /**
     * Pentasecurity ISign+ 등록 여부. 비밀 필드 없음 — 전부 평문 노출.
     * 운영자가 보통 serverUrl + agentId 만 입력. paths 는 default 그대로 동작.
     */
    isign: z
        .object({
        serverUrl: z.string(),
        agentId: z.string(),
        /** ISign+ OAuth 모드 플래그. '0' = OAuth 미사용 (기본). 로그인 URL 에 useOauth 쿼리로 부착. */
        useOauth: z.string(),
        requestData: z.string(),
        tokenAuthorizationPath: z.string(),
        loginPath: z.string(),
        logoutPath: z.string(),
        checkServerPath: z.string(),
        saveTokenPath: z.string(),
    })
        .nullable(),
});
export const AuthConfigUpdateRequestSchema = z.object({
    /** undefined 면 변경 안 함. activate 시 미설정 provider 면 서버가 거부. */
    activeExternalProvider: ExternalAuthProviderSchema.nullable().optional(),
    /** 줄당 1 CIDR. 빈 줄 무시. 빈 배열은 거부 (전부 차단되면 admin 락아웃). */
    localAdminIpAllowlist: z.array(z.string().min(1)).optional(),
    adminPagesIpRestricted: z.boolean().optional(),
    ssoAutoRedirect: z.boolean().optional(),
    /** 현재 IP 가 새 allowlist 에 없어도 그대로 저장. UI 의 confirm 후 사용. */
    acceptSelfLockoutRisk: z.boolean().optional(),
});
export const MicrosoftEntraUpdateRequestSchema = z.object({
    tenantId: z.string().min(1),
    clientId: z.string().min(1),
    /** 비어있으면 기존 secret 보존. 최초 등록 시에는 필수. */
    clientSecret: z.string().optional(),
});
export const MicrosoftEntraTestRequestSchema = z.object({
    tenantId: z.string().min(1),
    clientId: z.string().min(1),
    /** 비어있으면 저장된 secret 으로 테스트. */
    clientSecret: z.string().optional(),
});
export const MicrosoftEntraTestResponseSchema = z.object({
    ok: z.boolean(),
    message: z.string(),
    /** 디스커버리 성공 시 issuer URL. */
    issuer: z.string().optional(),
});
/** Login 페이지가 호출하는 public endpoint — 활성 외부 provider 만 노출. */
export const AuthProvidersResponseSchema = z.object({
    activeExternalProvider: ExternalAuthProviderSchema.nullable(),
    /** true 면 Login 페이지가 진입 즉시 `/api/auth/<provider>/start` 로 자동 navigate. */
    ssoAutoRedirect: z.boolean(),
    microsoft: z.object({ configured: z.boolean() }).nullable(),
    ldap: z.object({ configured: z.boolean() }).nullable(),
    isign: z.object({ configured: z.boolean() }).nullable(),
});
// ── LDAP ─────────────────────────────────────────────────────────────────────
/** LDAP bind 모드. search = service account bind 후 사용자 검색 → user-bind. direct = 사용자 ID 로 바로 bind, search 없음 (AD NetBIOS 등). */
export const LdapBindModeSchema = z.enum(['search', 'direct']);
// 원본 strict 정의 (search 모드 전제). bindMode 추가로 인해 mode 별 conditional refine 으로 대체:
// export const LdapConfigUpdateRequestSchema = z.object({
//   url: z.string().regex(/^ldaps?:\/\//, 'url 은 ldap:// 또는 ldaps:// 로 시작해야 합니다.'),
//   baseDN: z.string().min(1),
//   bindDN: z.string().min(1),
//   bindCredential: z.string().optional(),
//   userSearchFilter: z.string().min(1).refine((v) => v.includes('{username}'), {
//     message: 'userSearchFilter 에 {username} placeholder 가 필요합니다.',
//   }),
//   emailAttribute: z.string().min(1),
//   displayNameAttribute: z.string().min(1),
//   startTLS: z.boolean(),
// });
export const LdapConfigUpdateRequestSchema = z
    .object({
    url: z.string().regex(/^ldaps?:\/\//, 'url 은 ldap:// 또는 ldaps:// 로 시작해야 합니다.'),
    // 모드별 필수 여부가 달라 base 단계는 모두 z.string() 으로 두고, 아래 superRefine 에서 mode 별 검사.
    baseDN: z.string(),
    // [임시 — service account 미발급 환경 대응] anonymous bind 허용 LDAP 에서 빈 값 저장 가능하도록 필수 해제.
    // bindDN: z.string().min(1),
    bindDN: z.string(),
    /** 비어있으면 기존 bind 비밀번호 보존. search 모드 + service account 있으면 최초 등록 시 필수. */
    bindCredential: z.string().optional(),
    userSearchFilter: z.string(),
    emailAttribute: z.string(),
    displayNameAttribute: z.string(),
    startTLS: z.boolean(),
    bindMode: LdapBindModeSchema.default('search'),
    /** direct 모드 전용. `{username}` placeholder 가 폼 입력 이메일/사번으로 치환됨. */
    directBindFormat: z.string().optional(),
})
    .superRefine((val, ctx) => {
    if (val.bindMode === 'search') {
        if (!val.baseDN.trim()) {
            ctx.addIssue({
                code: 'custom',
                path: ['baseDN'],
                message: 'search 모드에서는 baseDN 이 필요합니다.',
            });
        }
        if (!val.userSearchFilter.includes('{username}')) {
            ctx.addIssue({
                code: 'custom',
                path: ['userSearchFilter'],
                message: 'search 모드에서는 userSearchFilter 에 {username} placeholder 가 필요합니다.',
            });
        }
        if (!val.emailAttribute.trim()) {
            ctx.addIssue({
                code: 'custom',
                path: ['emailAttribute'],
                message: 'search 모드에서는 emailAttribute 가 필요합니다.',
            });
        }
        if (!val.displayNameAttribute.trim()) {
            ctx.addIssue({
                code: 'custom',
                path: ['displayNameAttribute'],
                message: 'search 모드에서는 displayNameAttribute 가 필요합니다.',
            });
        }
    }
    else if (val.bindMode === 'direct') {
        if (!val.directBindFormat || !val.directBindFormat.includes('{username}')) {
            ctx.addIssue({
                code: 'custom',
                path: ['directBindFormat'],
                message: 'direct 모드에서는 directBindFormat 에 {username} placeholder 가 필요합니다.',
            });
        }
    }
});
export const LdapConfigTestRequestSchema = z.object({
    url: z.string().regex(/^ldaps?:\/\//),
    baseDN: z.string().min(1),
    bindDN: z.string().min(1),
    bindCredential: z.string().optional(),
    startTLS: z.boolean(),
});
export const LdapConfigTestResponseSchema = z.object({
    ok: z.boolean(),
    message: z.string(),
});
// ── ISign+ (Pentasecurity) ───────────────────────────────────────────────────
/**
 * Pentasecurity ISign+ 설정 업데이트 요청.
 * 모든 필드 평문 (비밀 0). paths 는 기본값 있어 운영자가 굳이 안 건드려도 됨.
 */
export const IsignConfigUpdateRequestSchema = z.object({
    /** ISign+ Appliance base URL. 예: https://sso.skpsnm.com */
    serverUrl: z
        .string()
        .regex(/^https?:\/\//, 'serverUrl 은 http:// 또는 https:// 로 시작해야 합니다.'),
    /** ISign+ 운영 콘솔에서 본 앱에 발급된 식별자. 비밀 아님. */
    agentId: z.string().min(1),
    /** ISign+ OAuth 모드 플래그. '0' = OAuth 미사용 (기본). 로그인 URL 에 useOauth 쿼리로 부착. */
    useOauth: z.string().min(1).default('0'),
    /** 응답에 받아올 사용자 속성 (콤마 구분). 기본 'id,sAMAccountName'. */
    requestData: z.string().min(1).default('id,sAMAccountName'),
    /** 토큰 검증 endpoint 경로. 기본 '/token/authorization'. */
    tokenAuthorizationPath: z.string().min(1).default('/token/authorization'),
    /** ISign+ 로그인 페이지 경로. 기본 '/login.html'. */
    loginPath: z.string().min(1).default('/login.html'),
    /** ISign+ 로그아웃 경로. 기본 '/logout.html'. */
    logoutPath: z.string().min(1).default('/logout.html'),
    /** ISign+ 서버 헬스체크 경로. 기본 '/openapi/checkserver'. */
    checkServerPath: z.string().min(1).default('/openapi/checkserver'),
    /** TAP 환경에서 프런트가 별도 호출하는 토큰 저장 경로. 기본 '/token/saveToken.html'. */
    saveTokenPath: z.string().min(1).default('/token/saveToken.html'),
});
/**
 * 연결 테스트 — `GET <serverUrl><checkServerPath>?agentId=<id>` 호출해 응답 검증.
 */
export const IsignConfigTestRequestSchema = z.object({
    serverUrl: z.string().regex(/^https?:\/\//),
    agentId: z.string().min(1),
    checkServerPath: z.string().min(1).default('/openapi/checkserver'),
});
export const IsignConfigTestResponseSchema = z.object({
    ok: z.boolean(),
    message: z.string(),
});
// ── Logout ───────────────────────────────────────────────────────────────────
/**
 * `POST /api/auth/logout` 응답. ok 외에 SSO provider 가 자체 로그아웃 페이지를
 * 가진 경우 (예: ISign+) `redirectUrl` 을 돌려준다. 프런트가 로컬 세션 정리 후
 * 그 URL 로 navigate 해 외부 IdP 세션도 같이 만료시킴.
 */
export const LogoutResponseSchema = z.object({
    ok: z.boolean(),
    /** 외부 IdP 의 로그아웃 페이지. ISign+ / 향후 SAML SLO 등에서 사용. */
    redirectUrl: z.string().optional(),
});
//# sourceMappingURL=auth.js.map